import { computed, ref } from 'vue';

import { defineStore } from 'pinia';

import elEn from 'element-plus/es/locale/lang/en';
import elEs from 'element-plus/es/locale/lang/es';
import elZhCn from 'element-plus/es/locale/lang/zh-cn';
import debounce from 'lodash/debounce';

import { DEFAULT_LOCALE, SUPPORTED_CODES, SUPPORTED_LOCALES, getLocale } from '@/config/locales';
import { setItemToLocalStorage } from '@/helpers/localStorageServices.js';
import dayjs from '@/plugins/dayjs';
import i18n, { loadLocaleMessages } from '@/plugins/i18n';
import { getUserSettingsByPage, upsertUserSetting } from '@/services/api/userSettingsApi';

const USER_LANGUAGE = 'user-language';
const SETTINGS_PAGE = 'profile';
const SETTINGS_KEY = 'language';

// Element Plus locale objects, keyed by our `code`. Drives date-pickers, pagination and the
// built-in validation strings of EP components rendered under <ElConfigProvider>.
const EP_LOCALES = { en: elEn, zh: elZhCn, es: elEs };

export const useLanguageStore = defineStore('language', () => {
  // i18n already resolved the initial locale (localStorage → browser → default) — mirror it so
  // the store and vue-i18n never disagree.
  const currentLanguage = ref(i18n.global.locale.value);

  const elementPlusLocale = computed(
    () => EP_LOCALES[currentLanguage.value] || EP_LOCALES[DEFAULT_LOCALE],
  );

  // Apply a language to every consumer (vue-i18n, localStorage cache, <html lang>, dayjs, EP).
  // No server write — used both by setLanguage and by hydration-from-server. Async because
  // locale messages are lazy-loaded: await the chunk before switching so the new language renders
  // with its strings rather than briefly falling back to English.
  const applyLanguage = async (code) => {
    const reg = getLocale(code);
    await loadLocaleMessages(reg.code);
    currentLanguage.value = reg.code;
    i18n.global.locale.value = reg.code;
    setItemToLocalStorage(USER_LANGUAGE, reg.code);
    document.documentElement.setAttribute('lang', reg.code);
    dayjs.locale(reg.dayjs);
  };

  const persistServer = debounce(async (code) => {
    // Skip the backend write when nobody is logged in (auth pages: login/register/forgot) —
    // user-settings is an authenticated endpoint, so this would 401. The choice stays in
    // localStorage and is pushed up on the next authenticated load (initializeLanguageFromServer).
    const { useAuthStore } = await import('@/stores/auth');
    if (!useAuthStore().isAuthenticated) return;
    upsertUserSetting({
      page_name: SETTINGS_PAGE,
      key: SETTINGS_KEY,
      value: code,
      is_personal: true,
    }).catch((e) => {
      console.warn('[languageStore] Server save failed (kept in localStorage):', e);
    });
  }, 800);

  // Public entry point for an explicit user choice.
  const setLanguage = (code) => {
    if (!SUPPORTED_CODES.includes(code)) return;
    persistServer(code);
    return applyLanguage(code);
  };

  // Local init (App.vue onBeforeMount): sync dayjs/<html lang> with the already-resolved locale.
  const initializeLanguage = () => applyLanguage(currentLanguage.value);

  // Dedup: only hit the server once per app token. Reset on failure so a later trigger retries.
  let lastLoadedToken = null;

  // Hydrate from the backend after login (rides App.vue's accessToken watcher, like blurStore).
  const initializeLanguageFromServer = async () => {
    const { useAuthStore } = await import('@/stores/auth');
    const token = useAuthStore().accessToken;
    if (!token || token === lastLoadedToken) return;
    lastLoadedToken = token;

    try {
      const res = await getUserSettingsByPage(SETTINGS_PAGE, { key: SETTINGS_KEY });
      const items = res?.data?.items || [];
      const value = items[0]?.value;
      if (value && SUPPORTED_CODES.includes(value)) {
        // Server holds the authoritative choice — apply it (localStorage acts as cache).
        if (value !== currentLanguage.value) await applyLanguage(value);
      } else if (currentLanguage.value !== DEFAULT_LOCALE) {
        // No server value yet, but the user picked a non-default language before logging in
        // (selector on the auth pages) — push that choice up so it follows them across devices.
        persistServer(currentLanguage.value);
      }
    } catch (e) {
      lastLoadedToken = null; // allow retry on the next trigger
      console.warn('[languageStore] Server load failed (using localStorage):', e);
    }
  };

  return {
    languages: SUPPORTED_LOCALES,
    currentLanguage,
    elementPlusLocale,
    setLanguage,
    initializeLanguage,
    initializeLanguageFromServer,
  };
});
